import { createApp, configureCompat } from 'vue';
import { createI18n } from 'vue-i18n';
import { createPinia } from 'pinia';
import { useMainStore } from "./state/store";
import { createORM } from "pinia-orm";
import { useNotificationsStore } from "./state/modules/notifications";
import { ADD_NOTIFICATION } from "./state/mutation-types";
import { createHead, VueHeadMixin } from '@unhead/vue/client'
import appConfig from './config/app';
import router from './routing/router';
import bus from "../shared/eventbus";
import moment from 'moment';
import numberAbbreviation from "./plugins/numberAbbreviation";
import NotificationGroup from './components/notifications/NotificationGroup';
import UnsavedChanges from './components/ui/UnsavedChanges';
import VuePermissions from './plugins/vuePermissions';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginVue from '@bugsnag/plugin-vue';

Bugsnag.start({
    appVersion: 'e1732384',
    apiKey: '9847cca1248af089ac5e9f57a979e091',
    plugins: [new BugsnagPluginVue()],
    releaseStage: Jobtoolz.environment,
    user: {
        id: Jobtoolz.user?.id || null,
        email: Jobtoolz.user?.attributes.email || null,
        name: Jobtoolz.user?.attributes.name || null
    }
});

const bugsnagVue = Bugsnag.getPlugin('vue');

// set global compatibility mode options
configureCompat({
    ATTR_FALSE_VALUE: false,
    COMPONENT_ASYNC: false,
});

export default function createJobtoolzApp() {
    const pinia = createPinia().use(createORM());
    const head = createHead();

    const app = createApp({
        watch: {
            '$route'(to) {
                if (to.meta?.reload) {
                    window.location.reload();
                }
            }
        },
        head() {
            const mainStore = useMainStore(pinia);
            const locale = mainStore.locale ?? appConfig.config.default.locale;
            return {
                htmlAttrs: {
                    lang: locale
                }
            };
        }
    });

    app.use(bugsnagVue);
    app.use(pinia);
    app.use(router);
    app.use(VuePermissions);
    app.use(numberAbbreviation);

    app.use(head);
    app.mixin(VueHeadMixin);

    const translationPlugin = translation();
    app.use(translationPlugin);
    setBusListener(translationPlugin);

    setDirectives(app);
    setComponents(app);
    setProperties(app);

    return app;
}

function setDirectives(app) {

    // custom directive to handle when an element is in the viewport
    app.directive('in-viewport', {
        beforeMount(el, binding) {
            if (typeof binding.value !== "function") {
                return;
            }
            const observer = new IntersectionObserver(entries => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        binding.value(false);
                    }
                });
            });
            observer.observe(el);
        }
    });
}

function setComponents(app) {
    app.component('notifications', NotificationGroup);
    app.component('unsaved-changes', UnsavedChanges);
}

function setProperties(app) {
    const mainStore = useMainStore();

    // return the translation for the locale from a translation object, i.e. {"en": "English", "nl": "Dutch"}
    app.config.globalProperties.translated = function(translations, locale, useFallback = false) {
        const fallbackLocale = mainStore.locale ?? appConfig.config.default.locale;
        const defaultLocale = mainStore.currentCompany?.jobsite.default_lang ?? fallbackLocale;
        return window.translated(translations, locale || fallbackLocale, useFallback, defaultLocale);
    };

    // return a description of the time from now, i.e., in an hour, a minute ago
    app.config.globalProperties.timeAgo = function(date) {
        const locale = mainStore.locale ?? appConfig.config.default.locale;
        const dateFormat = mainStore.dateFormat ?? appConfig.config.default.dateFormat;
        const oneWeekAgo = moment().subtract(1, 'w');
        const oneYearAgo = moment().subtract(1, 'y');
        const formattedDate = moment(date).locale(locale);
        return formattedDate.isAfter(oneWeekAgo)
            ? formattedDate.fromNow()
            : (formattedDate.isAfter(oneYearAgo)
                ? formattedDate.format("MMMM Do")
                : formattedDate.format(dateFormat));
    };
}

function translation() {
    const mainStore = useMainStore();
    const defaultLocale = mainStore.locale ?? appConfig.config.default.locale;
    const locale = Jobtoolz.user && Jobtoolz.user.attributes.language ? Jobtoolz.user.attributes.language : defaultLocale;
    return createTranslation(locale, ['en', 'nl']);
}

function createTranslation(locale, fallbackLocale) {
    return createI18n({
        locale: locale,
        allowComposition: true,
        messages: window.translation_labels,
        fallbackLocale: fallbackLocale,
    });
}

function setBusListener(translator) {
    bus.addEventListener('copied-to-clipboard', function(e) {
        const notificationStore = useNotificationsStore();
        notificationStore[ADD_NOTIFICATION]({
            type: 'notification',
            message: translator.global.t('general.copied_to_clipboard', { subject: e.detail }),
        });
    });
}
